import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaSave, FaEdit, FaPlus, FaTrash, FaHome, FaUser, FaCode } from 'react-icons/fa';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

const ContentManager = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Home Page Content
  const [homeContent, setHomeContent] = useState({
    title: '',
    subtitle: '',
    description: '',
    ctaText: '',
    ctaLink: '',
    rotatingWords: [],
    techStack: [],
    stats: {
      experience: '',
      projects: '',
      technologies: '',
      satisfaction: ''
    }
  });

  // About Page Content
  const [aboutContent, setAboutContent] = useState({
    title: '',
    subtitle: '',
    bio: '',
    mission: '',
    expertise: [],
    achievements: [],
    personalInfo: {
      location: '',
      availability: '',
      languages: []
    }
  });

  // Skills Section
  const [skills, setSkills] = useState({
    categories: [
      {
        name: 'Frontend Development',
        skills: [],
        icon: 'code'
      },
      {
        name: 'Backend Development',
        skills: [],
        icon: 'server'
      },
      {
        name: 'AI & Machine Learning',
        skills: [],
        icon: 'brain'
      }
    ]
  });

  useEffect(() => {
    fetchContent();
  }, [activeSection]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'content', activeSection);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (activeSection === 'home') {
          setHomeContent(data);
        } else if (activeSection === 'about') {
          setAboutContent(data);
        } else if (activeSection === 'skills') {
          setSkills(data);
        }
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let contentToSave;
      if (activeSection === 'home') {
        contentToSave = homeContent;
      } else if (activeSection === 'about') {
        contentToSave = aboutContent;
      } else if (activeSection === 'skills') {
        contentToSave = skills;
      }

      await setDoc(doc(db, 'content', activeSection), {
        ...contentToSave,
        updatedAt: new Date().toISOString()
      });
      
      alert(`${activeSection.charAt(0).toUpperCase() + activeSection.slice(1)} content saved successfully!`);
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Error saving content. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Add item to array (for rotating words, tech stack, etc.)
  const addArrayItem = (section, field, value) => {
    if (!value.trim()) return;
    
    if (section === 'home') {
      setHomeContent(prev => ({
        ...prev,
        [field]: [...prev[field], value]
      }));
    } else if (section === 'about') {
      setAboutContent(prev => ({
        ...prev,
        [field]: [...prev[field], value]
      }));
    }
  };

  // Remove item from array
  const removeArrayItem = (section, field, index) => {
    if (section === 'home') {
      setHomeContent(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
      }));
    } else if (section === 'about') {
      setAboutContent(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
      }));
    }
  };

  // Add skill category
  const addSkillCategory = () => {
    setSkills(prev => ({
      ...prev,
      categories: [...prev.categories, { name: '', skills: [], icon: 'code' }]
    }));
  };

  // Update skill category
  const updateSkillCategory = (index, field, value) => {
    setSkills(prev => ({
      ...prev,
      categories: prev.categories.map((cat, i) => 
        i === index ? { ...cat, [field]: value } : cat
      )
    }));
  };

  // Add skill to category
  const addSkillToCategory = (categoryIndex, skillName) => {
    if (!skillName.trim()) return;
    
    setSkills(prev => ({
      ...prev,
      categories: prev.categories.map((cat, i) => 
        i === categoryIndex 
          ? { ...cat, skills: [...cat.skills, { name: skillName, level: 80 }] }
          : cat
      )
    }));
  };

  // Remove skill from category
  const removeSkillFromCategory = (categoryIndex, skillIndex) => {
    setSkills(prev => ({
      ...prev,
      categories: prev.categories.map((cat, i) => 
        i === categoryIndex 
          ? { ...cat, skills: cat.skills.filter((_, si) => si !== skillIndex) }
          : cat
      )
    }));
  };

  const sections = [
    { id: 'home', label: 'Home Page', icon: FaHome },
    { id: 'about', label: 'About Page', icon: FaUser },
    { id: 'skills', label: 'Skills', icon: FaCode },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Content Manager</h2>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white px-6 py-2 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
        >
          <FaSave />
          {saving ? 'Saving...' : 'Save Changes'}
        </motion.button>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-all ${
              activeSection === section.id
                ? 'bg-violet-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <section.icon />
            {section.label}
          </button>
        ))}
      </div>

      {/* Home Page Content */}
      {activeSection === 'home' && (
        <div className="space-y-6 bg-gray-800 p-6 rounded-lg">
          <div>
            <label className="block text-gray-300 mb-2">Title</label>
            <input
              type="text"
              value={homeContent.title}
              onChange={(e) => setHomeContent({ ...homeContent, title: e.target.value })}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
              placeholder="e.g., Full Stack + Gen AI Developer"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Subtitle</label>
            <input
              type="text"
              value={homeContent.subtitle}
              onChange={(e) => setHomeContent({ ...homeContent, subtitle: e.target.value })}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
              placeholder="e.g., Building the Future with AI"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Description</label>
            <textarea
              value={homeContent.description}
              onChange={(e) => setHomeContent({ ...homeContent, description: e.target.value })}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none h-32"
              placeholder="Brief introduction about yourself..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">CTA Button Text</label>
              <input
                type="text"
                value={homeContent.ctaText}
                onChange={(e) => setHomeContent({ ...homeContent, ctaText: e.target.value })}
                className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                placeholder="e.g., View Projects"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">CTA Button Link</label>
              <input
                type="text"
                value={homeContent.ctaLink}
                onChange={(e) => setHomeContent({ ...homeContent, ctaLink: e.target.value })}
                className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                placeholder="e.g., /projects"
              />
            </div>
          </div>

          {/* Rotating Words */}
          <div>
            <label className="block text-gray-300 mb-2">Rotating Words (Hero Animation)</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                id="rotatingWordInput"
                className="flex-1 bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                placeholder="Add a word..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addArrayItem('home', 'rotatingWords', e.target.value);
                    e.target.value = '';
                  }
                }}
              />
              <button
                onClick={() => {
                  const input = document.getElementById('rotatingWordInput');
                  addArrayItem('home', 'rotatingWords', input.value);
                  input.value = '';
                }}
                className="bg-violet-500 text-white px-4 py-2 rounded-lg hover:bg-violet-600"
              >
                <FaPlus />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {homeContent.rotatingWords?.map((word, index) => (
                <span
                  key={index}
                  className="bg-gray-900 text-white px-3 py-1 rounded-full flex items-center gap-2"
                >
                  {word}
                  <button
                    onClick={() => removeArrayItem('home', 'rotatingWords', index)}
                    className="text-red-400 hover:text-red-500"
                  >
                    <FaTrash size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Tech Stack */}
          <div>
            <label className="block text-gray-300 mb-2">Tech Stack</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                id="techStackInput"
                className="flex-1 bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                placeholder="Add a technology..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addArrayItem('home', 'techStack', e.target.value);
                    e.target.value = '';
                  }
                }}
              />
              <button
                onClick={() => {
                  const input = document.getElementById('techStackInput');
                  addArrayItem('home', 'techStack', input.value);
                  input.value = '';
                }}
                className="bg-violet-500 text-white px-4 py-2 rounded-lg hover:bg-violet-600"
              >
                <FaPlus />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {homeContent.techStack?.map((tech, index) => (
                <span
                  key={index}
                  className="bg-gray-900 text-white px-3 py-1 rounded-full flex items-center gap-2"
                >
                  {tech}
                  <button
                    onClick={() => removeArrayItem('home', 'techStack', index)}
                    className="text-red-400 hover:text-red-500"
                  >
                    <FaTrash size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div>
            <label className="block text-gray-300 mb-3 text-lg font-semibold">Stats Section</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Years of Experience</label>
                <input
                  type="text"
                  value={homeContent.stats?.experience || ''}
                  onChange={(e) => setHomeContent({
                    ...homeContent,
                    stats: { ...homeContent.stats, experience: e.target.value }
                  })}
                  className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                  placeholder="e.g., 5+"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Projects Completed</label>
                <input
                  type="text"
                  value={homeContent.stats?.projects || ''}
                  onChange={(e) => setHomeContent({
                    ...homeContent,
                    stats: { ...homeContent.stats, projects: e.target.value }
                  })}
                  className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                  placeholder="e.g., 50+"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Technologies Mastered</label>
                <input
                  type="text"
                  value={homeContent.stats?.technologies || ''}
                  onChange={(e) => setHomeContent({
                    ...homeContent,
                    stats: { ...homeContent.stats, technologies: e.target.value }
                  })}
                  className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                  placeholder="e.g., 30+"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Client Satisfaction</label>
                <input
                  type="text"
                  value={homeContent.stats?.satisfaction || ''}
                  onChange={(e) => setHomeContent({
                    ...homeContent,
                    stats: { ...homeContent.stats, satisfaction: e.target.value }
                  })}
                  className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                  placeholder="e.g., 100%"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* About Page Content */}
      {activeSection === 'about' && (
        <div className="space-y-6 bg-gray-800 p-6 rounded-lg">
          <div>
            <label className="block text-gray-300 mb-2">Page Title</label>
            <input
              type="text"
              value={aboutContent.title}
              onChange={(e) => setAboutContent({ ...aboutContent, title: e.target.value })}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
              placeholder="e.g., About Me"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Subtitle</label>
            <input
              type="text"
              value={aboutContent.subtitle}
              onChange={(e) => setAboutContent({ ...aboutContent, subtitle: e.target.value })}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
              placeholder="e.g., Full Stack & AI Engineer"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Bio</label>
            <textarea
              value={aboutContent.bio}
              onChange={(e) => setAboutContent({ ...aboutContent, bio: e.target.value })}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none h-40"
              placeholder="Tell your story..."
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Mission Statement</label>
            <textarea
              value={aboutContent.mission}
              onChange={(e) => setAboutContent({ ...aboutContent, mission: e.target.value })}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none h-24"
              placeholder="Your professional mission..."
            />
          </div>

          {/* Expertise Areas */}
          <div>
            <label className="block text-gray-300 mb-2">Expertise Areas</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                id="expertiseInput"
                className="flex-1 bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                placeholder="Add expertise..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addArrayItem('about', 'expertise', e.target.value);
                    e.target.value = '';
                  }
                }}
              />
              <button
                onClick={() => {
                  const input = document.getElementById('expertiseInput');
                  addArrayItem('about', 'expertise', input.value);
                  input.value = '';
                }}
                className="bg-violet-500 text-white px-4 py-2 rounded-lg hover:bg-violet-600"
              >
                <FaPlus />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {aboutContent.expertise?.map((item, index) => (
                <span
                  key={index}
                  className="bg-gray-900 text-white px-3 py-1 rounded-full flex items-center gap-2"
                >
                  {item}
                  <button
                    onClick={() => removeArrayItem('about', 'expertise', index)}
                    className="text-red-400 hover:text-red-500"
                  >
                    <FaTrash size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Achievements */}
          <div>
            <label className="block text-gray-300 mb-2">Key Achievements</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                id="achievementInput"
                className="flex-1 bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                placeholder="Add achievement..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addArrayItem('about', 'achievements', e.target.value);
                    e.target.value = '';
                  }
                }}
              />
              <button
                onClick={() => {
                  const input = document.getElementById('achievementInput');
                  addArrayItem('about', 'achievements', input.value);
                  input.value = '';
                }}
                className="bg-violet-500 text-white px-4 py-2 rounded-lg hover:bg-violet-600"
              >
                <FaPlus />
              </button>
            </div>
            <div className="space-y-2">
              {aboutContent.achievements?.map((achievement, index) => (
                <div
                  key={index}
                  className="bg-gray-900 text-white px-3 py-2 rounded-lg flex justify-between items-center"
                >
                  <span>{achievement}</span>
                  <button
                    onClick={() => removeArrayItem('about', 'achievements', index)}
                    className="text-red-400 hover:text-red-500"
                  >
                    <FaTrash size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Personal Info */}
          <div>
            <label className="block text-gray-300 mb-3 text-lg font-semibold">Personal Information</label>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Location</label>
                <input
                  type="text"
                  value={aboutContent.personalInfo?.location || ''}
                  onChange={(e) => setAboutContent({
                    ...aboutContent,
                    personalInfo: { ...aboutContent.personalInfo, location: e.target.value }
                  })}
                  className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                  placeholder="e.g., San Francisco, CA"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Availability</label>
                <input
                  type="text"
                  value={aboutContent.personalInfo?.availability || ''}
                  onChange={(e) => setAboutContent({
                    ...aboutContent,
                    personalInfo: { ...aboutContent.personalInfo, availability: e.target.value }
                  })}
                  className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                  placeholder="e.g., Available for freelance"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Skills Section */}
      {activeSection === 'skills' && (
        <div className="space-y-6 bg-gray-800 p-6 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-white">Skill Categories</h3>
            <button
              onClick={addSkillCategory}
              className="flex items-center gap-2 bg-violet-500 text-white px-4 py-2 rounded-lg hover:bg-violet-600"
            >
              <FaPlus /> Add Category
            </button>
          </div>

          {skills.categories?.map((category, catIndex) => (
            <div key={catIndex} className="bg-gray-900 p-4 rounded-lg space-y-4">
              <div className="flex gap-4">
                <input
                  type="text"
                  value={category.name}
                  onChange={(e) => updateSkillCategory(catIndex, 'name', e.target.value)}
                  className="flex-1 bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                  placeholder="Category name..."
                />
                <input
                  type="text"
                  value={category.icon}
                  onChange={(e) => updateSkillCategory(catIndex, 'icon', e.target.value)}
                  className="w-32 bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                  placeholder="Icon name"
                />
                <button
                  onClick={() => {
                    setSkills(prev => ({
                      ...prev,
                      categories: prev.categories.filter((_, i) => i !== catIndex)
                    }));
                  }}
                  className="text-red-400 hover:text-red-500 px-3"
                >
                  <FaTrash />
                </button>
              </div>

              {/* Skills in this category */}
              <div>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    id={`skill-${catIndex}`}
                    className="flex-1 bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                    placeholder="Add skill..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addSkillToCategory(catIndex, e.target.value);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById(`skill-${catIndex}`);
                      addSkillToCategory(catIndex, input.value);
                      input.value = '';
                    }}
                    className="bg-violet-500 text-white px-4 py-2 rounded-lg hover:bg-violet-600"
                  >
                    <FaPlus />
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {category.skills?.map((skill, skillIndex) => (
                    <span
                      key={skillIndex}
                      className="bg-gray-800 text-white px-3 py-1 rounded-full flex items-center gap-2"
                    >
                      {skill.name || skill}
                      <button
                        onClick={() => removeSkillFromCategory(catIndex, skillIndex)}
                        className="text-red-400 hover:text-red-500"
                      >
                        <FaTrash size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContentManager;
